/**
 * Product Technical Specifications - JavaScript Controller
 * Handles tabs, accordion, and accessibility for technical specs section
 */

class ProductTechnicalSpecs extends HTMLElement {
  constructor() {
    super();
    this.layout = this.dataset.layout || 'tabs';
    this.tabs = [];
    this.panels = [];
    this.currentTab = 0;
  }

  connectedCallback() {
    this.initLayout();
  }

  initLayout() {
    if (this.layout === 'tabs') {
      this.initTabs();
    } else if (this.layout === 'accordion') {
      this.initAccordion();
    }
    // Stacked layout needs no JavaScript
  }

  initTabs() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.switchTab(index));
      tab.addEventListener('keydown', (e) => this.handleTabKeydown(e, index));
    });

    // Set initial active tab
    this.switchTab(0);
  }

  switchTab(index) {
    if (index === this.currentTab) return;

    // Hide current panel
    if (this.panels[this.currentTab]) {
      this.panels[this.currentTab].hidden = true;
      this.panels[this.currentTab].classList.remove('is-active');
    }

    // Deactivate current tab
    if (this.tabs[this.currentTab]) {
      this.tabs[this.currentTab].setAttribute('aria-selected', 'false');
      this.tabs[this.currentTab].classList.remove('is-active');
      this.tabs[this.currentTab].setAttribute('tabindex', '-1');
    }

    // Activate new tab
    this.currentTab = index;
    this.tabs[index].setAttribute('aria-selected', 'true');
    this.tabs[index].classList.add('is-active');
    this.tabs[index].setAttribute('tabindex', '0');
    this.tabs[index].focus();

    // Show new panel
    this.panels[index].hidden = false;
    this.panels[index].classList.add('is-active');
  }

  handleTabKeydown(event, index) {
    const key = event.key;
    let newIndex = index;

    switch (key) {
      case 'ArrowLeft':
        newIndex = index === 0 ? this.tabs.length - 1 : index - 1;
        break;
      case 'ArrowRight':
        newIndex = index === this.tabs.length - 1 ? 0 : index + 1;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.switchTab(newIndex);
  }

  initAccordion() {
    const accordionItems = this.querySelectorAll('.product-technical-specs__accordion-item');
    
    accordionItems.forEach((item) => {
      const summary = item.querySelector('.product-technical-specs__accordion-summary');
      
      if (!summary) return;

      // Enhance accessibility
      summary.addEventListener('click', (e) => {
        // Let native details/summary handle toggle
        // Add animation classes for smooth transitions
        requestAnimationFrame(() => {
          if (item.open) {
            item.classList.add('is-open');
          } else {
            item.classList.remove('is-open');
          }
        });
      });
    });
  }
}

customElements.define('product-technical-specs', ProductTechnicalSpecs);

document.addEventListener('DOMContentLoaded', function () {
  const tabsContainer = document.querySelector('.product-technical-specs__tabs');
  if (!tabsContainer) return;

  const tabButtons = tabsContainer.querySelectorAll('.product-technical-specs__tab-button');
  const tabPanels = tabsContainer.querySelectorAll('.product-technical-specs__tab-panel');

  tabButtons.forEach(function (button) {
    button.addEventListener('click', function () {
      const targetPanelId = this.getAttribute('aria-controls');

      tabButtons.forEach(function (btn) {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-selected', 'false');
      });

      tabPanels.forEach(function (panel) {
        panel.classList.remove('is-active');
        panel.setAttribute('hidden', '');
      });

      this.classList.add('is-active');
      this.setAttribute('aria-selected', 'true');

      const targetPanel = document.getElementById(targetPanelId);
      if (targetPanel) {
        targetPanel.classList.add('is-active');
        targetPanel.removeAttribute('hidden');
      }
    });

    button.addEventListener('keydown', function (e) {
      let newIndex;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        newIndex = Array.from(tabButtons).indexOf(this) + 1;
        if (newIndex >= tabButtons.length) newIndex = 0;
        tabButtons[newIndex].click();
        tabButtons[newIndex].focus();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        newIndex = Array.from(tabButtons).indexOf(this) - 1;
        if (newIndex < 0) newIndex = tabButtons.length - 1;
        tabButtons[newIndex].click();
        tabButtons[newIndex].focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        tabButtons[0].click();
        tabButtons[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        tabButtons[tabButtons.length - 1].click();
        tabButtons[tabButtons.length - 1].focus();
      }
    });
  });
});
